import { Injectable } from '@angular/core';
import { isBefore } from 'date-fns';
import { catchError, from, interval, Observable } from 'rxjs';
import { map, mergeMap, reduce, startWith, switchMap } from 'rxjs/operators';

import { AdminBackend } from '@libs/src/admin/admin.backend';

import { ActionItem } from '@libs/src/models/action-item.model';
import {
  ACTION_QUEUE_DEACTIVATE,
  ACTION_QUEUE_DELETE,
  ACTION_QUEUE_INVITE,
  ACTION_QUEUE_STATUS_CANCELLED,
  ACTION_QUEUE_STATUS_COMPLETED,
  ACTION_QUEUE_STATUS_ERROR,
  ACTION_QUEUE_STATUS_IN_PROGRESS,
  ACTION_QUEUE_STATUS_INITIALIZING,
  ACTION_QUEUE_STATUS_PENDING,
  ACTION_QUEUE_STATUS_SEMI_COMPLETED,
  ACTION_QUEUE_UPDATE,
  ActionQueue,
  IMPORT_HISTORY_STATUS_INCOMPLETE,
} from '@libs/src/models/action-queue.model';
import { Group } from '@libs/src/models/group.model';
import { ActionQueueFile } from '@main-client/src/app/members/members.component.helper';

const ACTION_QUEUE_POLL_INTERVAL = 40000;

@Injectable()
export class ActionQueueService {
  readonly nonCompletedActionQueueStatuses = [
    ACTION_QUEUE_STATUS_IN_PROGRESS,
    ACTION_QUEUE_STATUS_PENDING,
    ACTION_QUEUE_STATUS_INITIALIZING,
  ];
  constructor(private readonly adminBackend: AdminBackend) {}

  getActionQueues(params?: {
    fileName?: ActionQueue['flat_file_name'];
    flatFileGeneration?: ActionQueue['flat_file_generation'];
    groupId?: Group['_id'];
    status?: ActionQueue['status'];
  }) {
    return this.adminBackend.v3.getActionQueues(params);
  }

  getActionItemsForActionQueue(
    actionQueue: ActionQueue['_id'],
  ): Observable<{ actionItems: ActionItem[] }> {
    return this.adminBackend.v3
      .getActionItems(actionQueue)
      .pipe(catchError(() => []));
  }

  getActionItemsForActionQueues(
    actionQueues: ActionQueue[],
  ): Observable<{ [key: string]: ActionItem[] }> {
    return from(actionQueues).pipe(
      mergeMap((actionQueue) =>
        this.adminBackend.v3.getActionItems(actionQueue._id).pipe(
          catchError(() => []),
          map(({ actionItems }) => ({
            [actionQueue.action]: actionItems,
          })),
        ),
      ),
      reduce((acc, value) => ({ ...acc, ...value }), {}),
    );
  }

  pollActionQueues(params?: {
    fileName?: ActionQueue['flat_file_name'];
    flatFileGeneration?: ActionQueue['flat_file_generation'];
    groupId?: Group['_id'];
    status?: ActionQueue['status'];
  }) {
    return interval(ACTION_QUEUE_POLL_INTERVAL).pipe(
      startWith(0),
      switchMap(() => this.getActionQueues(params)),
    );
  }

  pollActionItemsForActionQueue(actionQueueId: ActionQueue['_id']) {
    return interval(ACTION_QUEUE_POLL_INTERVAL).pipe(
      startWith(0),
      switchMap(() => this.adminBackend.v3.getActionItems(actionQueueId)),
    );
  }

  handleActiveMembershipFileUpload(): Observable<{
    fileName: string;
    groupId: Group['_id'];
  }> {
    return this.getActionQueues().pipe(
      map((response: { actionQueues: ActionQueue[] }) => {
        const filteredActionQueues = response.actionQueues.filter(
          (actionQueue) =>
            [
              ACTION_QUEUE_DEACTIVATE,
              ACTION_QUEUE_DELETE,
              ACTION_QUEUE_INVITE,
              ACTION_QUEUE_UPDATE,
            ].includes(actionQueue.action),
        );
        const activeActionQueuesExist = filteredActionQueues.some(
          ({ status }) =>
            status === ACTION_QUEUE_STATUS_PENDING ||
            status === ACTION_QUEUE_STATUS_INITIALIZING ||
            status === ACTION_QUEUE_STATUS_IN_PROGRESS ||
            status === ACTION_QUEUE_STATUS_COMPLETED,
        );
        if (activeActionQueuesExist) {
          return {
            fileName: response.actionQueues[0].flat_file_name,
            groupId: response.actionQueues[0].group,
          };
        }
      }),
    );
  }

  getAllImportedFilesAndQueues(): Observable<ActionQueueFile[]> {
    return this.adminBackend.v3.getAllActionQueuesAndFiles().pipe(
      map(({ aumImportFileInfo }) =>
        aumImportFileInfo.map((file) => {
          const createdAt = file.action_queues[0].created_at;
          const actionQueuesStatus = file.action_queues.reduce(
            (actionQueues, queue) => {
              const date = isBefore(
                new Date(actionQueues.date),
                new Date(queue.updated_at),
              )
                ? queue.updated_at
                : actionQueues.date;
              const status = this.getImportHistoryStatus(
                queue.status,
                actionQueues.status,
              );
              return {
                date,
                status,
              };
            },
            { date: createdAt, status: '' },
          );
          return {
            account: { full_name: file.account?.full_name || '-' },
            created_at: createdAt,
            file_url: file.file_url,
            flat_file_generation: file.flat_file_generation,
            flat_file_name: file.flat_file_name,
            group: file.group,
            queues: file.action_queues,
            status: actionQueuesStatus.status,
            updated_at: actionQueuesStatus.date,
          } as ActionQueueFile;
        }),
      ),
    );
  }

  getImportHistoryStatus(actionQueueStatus: string, currentStatus: string) {
    if (
      [actionQueueStatus, currentStatus].includes(ACTION_QUEUE_STATUS_ERROR)
    ) {
      return ACTION_QUEUE_STATUS_ERROR;
    }
    if (
      this.nonCompletedActionQueueStatuses.includes(actionQueueStatus) ||
      this.nonCompletedActionQueueStatuses.includes(currentStatus)
    ) {
      return ACTION_QUEUE_STATUS_IN_PROGRESS;
    }
    if (
      [actionQueueStatus, currentStatus].includes(ACTION_QUEUE_STATUS_CANCELLED)
    ) {
      return ACTION_QUEUE_STATUS_CANCELLED;
    }
    if (
      ACTION_QUEUE_STATUS_SEMI_COMPLETED === actionQueueStatus ||
      IMPORT_HISTORY_STATUS_INCOMPLETE === currentStatus
    ) {
      return IMPORT_HISTORY_STATUS_INCOMPLETE;
    }
    return ACTION_QUEUE_STATUS_COMPLETED;
  }
}
